/**
 * Keeps keyboard focus on the equivalent control when the dialog switches between the rail and the compact bar (Option
 * C, C1, plan §3.5). Controls that exist in both layouts carry the same `data-focus-role`:
 * - `engine`: the rail items ↔ the compact engine select (the reverse lands on the chosen rail item, `aria-current`);
 * - `all-engines`: the rail's All engines ↔ the compact bar's;
 * - `entry`: the rail's entry points ↔ the compact bar's Add from… button.
 *
 * `capture()` must run before the new layout renders (the old control still has focus); the mapped control is focused
 * right after the switch commits.
 */
import { useLayoutEffect, useRef } from 'react';

export type FocusRole = 'engine' | 'all-engines' | 'entry';

export function focusRoleOf(element: Element | null): FocusRole | null {
  const role = element instanceof HTMLElement ? element.dataset.focusRole : undefined;
  return role === 'engine' || role === 'all-engines' || role === 'entry' ? role : null;
}

/** The control of `role` in the current layout: the current item first (the chosen rail engine), else the first. */
export function focusTargetFor(root: ParentNode, role: FocusRole): HTMLElement | null {
  return (
    root.querySelector<HTMLElement>(`[data-focus-role="${role}"][aria-current="true"]`) ??
    root.querySelector<HTMLElement>(`[data-focus-role="${role}"]`)
  );
}

export function useFocusRoleMapping(layoutKey: string): () => void {
  const pending = useRef<FocusRole | null>(null);
  const previous = useRef(layoutKey);

  useLayoutEffect(() => {
    if (previous.current === layoutKey) return;
    previous.current = layoutKey;
    const role = pending.current;
    pending.current = null;
    if (role) focusTargetFor(document, role)?.focus();
  }, [layoutKey]);

  return () => {
    pending.current = focusRoleOf(document.activeElement);
  };
}
